/**
 * @refresh reset
 *
 * Instructs Fast Refresh to remount App instead of trying to do HMR.
 * This was added since prevent errors when Live Reloading.
 *
 * TODO: Investigate a way to make HMR work with encompass instead of disabling it.
 */
import { useState } from 'react';

import {
  EncompassCodeEditor,
  EncompassLayout,
  EncompassProvider,
  EncompassTheme,
  EncompassVisualEditor,
  createEncompass,
  testComponents,
  components,
  useEncompass,
} from '@customerio/encompass';

const value = `<x-base>
<x-section>
{% if true %}
<x-heading-1>Welcome to the test page 👋</x-heading-1>
<p>hello world</p>
{% endif %}
<x-heading-2>Heading 2</x-heading-2>
<x-heading-3>Heading 3</x-heading-3>
<x-heading-4>Heading 4</x-heading-4>
<x-heading-5>Heading 5</x-heading-5>
<x-heading-6>Heading 6</x-heading-6>
<x-paragraph>Melted <span style="color: #F00">cheese</span> pepper jack cheese slices. St. agur blue cheese macaroni cheese croque monsieur cow gouda the big cheese cheeseburger emmental. Cheese triangles camembert de normandie fondue jarlsberg swiss cream feta.</x-paragraph>
<x-image src="https://picsum.photos/seed/300/600/300?grayscale"  alt="Image" />
<x-video
src="https://www.loom.com/share/76a6495895de487aa57cb6fa50fccf35"
alt="Video"
/>
<x-hr />
<x-image  alt="Image" />
<x-cta>Click Me</x-cta>
<x-paragraph>hello world</x-paragraph>
</x-section>
  </x-base>`;

const testValue = `<html>
<head></head>
<body>
  <container>
    <row>
      <column>
        <heading1>Welcome to the test page 👋</heading1>
      </column>
    </row>
    <row>
      <column>
        <paragraph>Melted cheese pepper jack cheese slices. St. agur blue cheese macaroni cheese croque monsieur cow gouda the big cheese cheeseburger emmental. Cheese triangles camembert de normandie fondue jarlsberg swiss cream feta.</paragraph>
        <paragraph>Danish fontina bavarian bergkase mozzarella gouda port-salut cauliflower cheese blue castello. Cheddar edam.</paragraph>

        <!-- <multi-slot>
          <fragment #slot="first">hello world</fragment>
          <fragment #slot="second">hello world</fragment>
        </multi-slot> -->

        <custom-button
          variant="primary"
          :size="'large'"
          :href="'#kinda'"
        >
          Click me
        </custom-button>
      </column>
      <column>
        <custom-image src="https://picsum.photos/seed/300/600/300?grayscale" alt="Placeholder" />
      </column>
    </row>
    <row>
      <column>
        <custom-image src="https://picsum.photos/seed/300/600/400?grayscale" alt="Placeholder" />
      </column>
      <column>
        <paragraph>Melted cheese pepper jack cheese slices. St. agur blue cheese macaroni cheese croque monsieur cow gouda the big cheese cheeseburger emmental. Cheese triangles camembert de normandie fondue jarlsberg swiss cream feta.</paragraph>
        <paragraph>Danish fontina bavarian bergkase mozzarella gouda port-salut cauliflower cheese blue castello. Cheddar edam.</paragraph>
        <custom-button
          variant="primary"
          :size="'large'"
          :href="'#kinda'"
        >
          Click me
        </custom-button>
      </column>
    </row>
  </container>
</body>
</html>`;

const isTest = window.location.pathname === '/test';

const encompass = createEncompass({
  codeEditor: {
    path: 'index.html',
    value: isTest ? testValue : value,
  },
  visualEditor: {
    globalComponents: isTest ? testComponents : components,
    defaultProseComponent: isTest ? 'paragraph' : 'x-paragraph',
    autofocus: true,
    cartaOptions: {
      personalizationLanguage: {
        textTokenizer: {
          patterns: [
            ['{%', '%}'],
            ['{{', '}}'],
          ],
          match(token: string) {
            // match {% if ... %} and {% for ... %}
            if (token.match(/^{%(?:-)?\s*(if|for)/)) {
              return 'open';
            }

            // match {% elsif ... %}
            if (token.match(/^{%(?:-)?\s*elsif/)) {
              return 'divider';
            }

            // match {% endif %} and {% endfor %}
            if (token.match(/^{%(?:-)?\s*(endif|endfor)/)) {
              return 'close';
            }

            return 'expression';
          },
          matchOpenClose(open: string, close: string) {
            // get the name of the tag
            const openName = open.match(/^{%(?:-)?\s*(\w+)/)?.[1];
            const closeName = close.match(/^{%(?:-)?\s*(\w+)/)?.[1];

            // if the names match, then we have a match
            return `end${openName}` === closeName;
          },
        },
      },
    },
    onSelectMedia: async () => {
      if (confirm('Upload a new image?')) {
        return 'https://picsum.photos/seed/300/600/300?grayscale';
      }

      return null;
    },
    onUploadMedia: async ({ data }) => {
      return data;
    },
  },
});

export function App() {
  return (
    <EncompassProvider encompass={encompass}>
      <EncompassTheme>
        <AppInner />
      </EncompassTheme>
    </EncompassProvider>
  );
}

function AppInner() {
  const { visualEditor } = useEncompass();
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [showInsertMenu, setShowInsertMenu] = useState(true);
  const [showLayers, setShowLayers] = useState(true);

  const showPropertiesMenu = visualEditor.state.fullPropertiesMenu.isVisible;

  const CodeEditorWrapper = showCodeEditor ? EncompassLayout.Panel : 'div';

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        background: '#F7F8FA',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'white',
          border: '1px solid #EBECF2',
          borderRadius: '4px',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            borderBottom: '1px solid #EBECF2',
            padding: '8px 16px',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <DisplayToggle
              label="Toggle Code Editor"
              icon="code"
              isActive={showCodeEditor}
              onClick={() => setShowCodeEditor(!showCodeEditor)}
            />
            <DisplayToggle
              label="Toggle Insert Menu"
              icon="add"
              isActive={showInsertMenu}
              onClick={() => setShowInsertMenu(!showInsertMenu)}
            />
            <DisplayToggle
              label="Toggle Layers"
              icon="layers"
              isActive={showLayers}
              onClick={() => setShowLayers(!showLayers)}
            />
          </div>
          <div>
            <DisplayToggle
              label="Copy HTML"
              icon="code"
              isActive={false}
              onClick={async () => {
                const html = await encompass.visualEditor.actions.getHTML();

                await navigator.clipboard.writeText(html);

                alert('Copied HTML to clipboard');
              }}
            />
            <DisplayToggle
              label="Undo"
              icon="undo"
              isActive={false}
              onClick={() => encompass.visualEditor.actions.undo()}
            />
            <DisplayToggle
              label="Redo"
              icon="redo"
              isActive={false}
              onClick={() => encompass.visualEditor.actions.redo()}
            />
            <DisplayToggle
              label="Toggle Properties Menu"
              icon="tune"
              isActive={showPropertiesMenu}
              onClick={() => {
                if (showPropertiesMenu) {
                  encompass.visualEditor.actions.hideFullPropertiesMenu();
                } else {
                  encompass.visualEditor.actions.showFullPropertiesMenu();
                }
              }}
            />
          </div>
        </div>
        <div
          style={{
            height: 0,
            flexGrow: 1,
          }}
        >
          <EncompassLayout.Root>
            <EncompassLayout.PanelGroup direction="horizontal">
              <>
                <CodeEditorWrapper
                  order={1}
                  style={{
                    display: showCodeEditor ? 'block' : 'none',
                  }}
                >
                  <EncompassCodeEditor
                    onInput={(value) => {
                      encompass.visualEditor.commands.setContent(value);
                    }}
                  />
                </CodeEditorWrapper>
                {showCodeEditor ? <EncompassLayout.ResizeHandle /> : null}
              </>
              {showLayers || showInsertMenu ? (
                <>
                  <EncompassLayout.Panel maxSizePx={600} defaultSizePx={300} minSizePx={200} order={2}>
                    <EncompassLayout.PanelGroup>
                      {showInsertMenu && (
                        <EncompassLayout.Panel style={{ height: '100%' }} order={1}>
                          <EncompassVisualEditor.InsertMenu />
                        </EncompassLayout.Panel>
                      )}
                      {showLayers && showInsertMenu && <EncompassLayout.ResizeHandle />}
                      {showLayers && (
                        <EncompassLayout.Panel order={2}>
                          <EncompassVisualEditor.Layers />
                        </EncompassLayout.Panel>
                      )}
                    </EncompassLayout.PanelGroup>
                  </EncompassLayout.Panel>
                  <EncompassLayout.ResizeHandle />
                </>
              ) : null}
              <EncompassLayout.Panel order={3}>
                <EncompassLayout.PanelGroup>
                  <EncompassVisualEditor.View />
                  <EncompassVisualEditor.ContextBar />
                </EncompassLayout.PanelGroup>
              </EncompassLayout.Panel>
              {showPropertiesMenu ? (
                <>
                  <EncompassLayout.ResizeHandle />
                  <EncompassLayout.Panel maxSizePx={600} defaultSizePx={320} minSizePx={320} order={4}>
                    <EncompassVisualEditor.FullPropertiesMenu />
                  </EncompassLayout.Panel>
                </>
              ) : null}
              <EncompassVisualEditor.CorePropertiesMenu />
            </EncompassLayout.PanelGroup>
          </EncompassLayout.Root>
        </div>
      </div>
    </div>
  );
}

function DisplayToggle({
  label,
  icon,
  onClick,
  isActive,
}: {
  label: string;
  icon: string;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <button
      style={{
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '4px',
        marginRight: '8px',
        background: isActive ? '#EBECF2' : 'none',
        fontSize: '20px',
        color: '#4B4B60',
      }}
      className="material-symbols-outlined"
      onClick={onClick}
      aria-label={label}
      data-encompass-area
    >
      {icon}
    </button>
  );
}
